import { EXIT_DATE_ALIAS, FUND_EXIT_ENTERPRISE_ALIAS, FUND_EXIT_EQUITY_ALIAS } from 'common/constants/allocations';
import { GRID_DATE, NUMBER } from 'common/constants/gridType';
import { getEnterpriseValueFormula, getEquityValueFormula } from 'common/formulas/math.js/ENTERPRISE_AND_EQUITY_VALUE';
import { GridType } from 'common/types/scalarSpreadsheet';
import { ICell, ParserParams } from 'models/interfaces/spreadsheet-types/core-types';
import {
  contentClass,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
  FOREIGN_EXCHANGE_RATE_BASE_ALIAS,
  FOREIGN_EXCHANGE_RATE_TARGET_ALIAS,
  FOREIGN_LESS_DEBT_ALIAS,
  FOREIGN_PLUS_CASH_ALIAS,
  LESS_DEBT_ALIAS,
  PLUS_CASH_ALIAS,
} from 'pages/Waterfall/SpreadsheetConfig/constants';
import { INPUT_SHEET_HEADER_ALIAS } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/constants';
import { InputSheetRow } from 'pages/Waterfall/SpreadsheetConfig/InputSheetConfig/InputSheetRowConfig/inputSheetRowConfig.props';
import { EXCHANGE_RATE_REFERENCE } from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/ExchangeRateSheet/ExchangeRateSheet.constants';
import { WaterfallFXRowLabel } from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/WaterfallFXRowLabel/WaterfallFXRowLabel';
import WaterfallFXValueViewer from 'pages/Waterfall/SpreadsheetConfig/WaterfallFX/WaterfallFXValueViewer/WaterfallFXValueViewer';

const CURRENCY_SELECTOR_ALIAS = 'fund_currency_selector';
const CONVERTED_CURRENCY_ALIASES = [
  PLUS_CASH_ALIAS,
  LESS_DEBT_ALIAS,
  EXIT_ENTERPRISE_VALUE_ALIAS,
  EXIT_EQUITY_ALIAS,
  FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
];
const ROW_LABEL_CLASS = 'row-label text-left';

const joinClassNames = (...classNames: Array<string | null | undefined>) => classNames.filter(Boolean).join(' ');

const InputSheetParser = ({ rowConfig }: ParserParams<object, object, object, object>) => {
  const cells: Record<string, ICell> = {};
  let enterpriseValueCell: ICell | undefined;
  let equityValueCell: ICell | undefined;
  let plusCashCell: ICell | undefined;
  let lessDebtCell: ICell | undefined;

  (rowConfig as InputSheetRow[]).forEach((row, index) => {
    const columnLegend = 'A';
    const valueColumnLegend = 'B';
    const foreignValueColumnLegend = 'C';
    const key = `${columnLegend}${index + 1}`;
    const valueKey = `${valueColumnLegend}${index + 1}`;
    const foreignValueKey = `${foreignValueColumnLegend}${index + 1}`;
    const isHeaderRow = row.alias === INPUT_SHEET_HEADER_ALIAS;
    const isConvertedCurrencyRow = CONVERTED_CURRENCY_ALIASES.includes(row.alias ?? '');
    const isExchangeRateRow = row.alias === FOREIGN_EXCHANGE_RATE_BASE_ALIAS;
    const contentCellClassName = joinClassNames(contentClass, row.className);
    const rowLabelClassName = joinClassNames(ROW_LABEL_CLASS, row.className, row.titleClassName);
    let gridType: GridType | undefined;
    let foreignValueViewer = row.valueViewer;
    let foreignValue: ICell['value'] = isHeaderRow ? '' : `=${valueKey}`;

    if (isConvertedCurrencyRow) {
      foreignValueViewer = WaterfallFXValueViewer;
      foreignValue = row.foreignValue === null ? null : `=${valueKey} * ${EXCHANGE_RATE_REFERENCE}`;
      gridType = NUMBER;
    } else if (isExchangeRateRow) {
      foreignValueViewer = WaterfallFXValueViewer;
      foreignValue = row.foreignValue;
      gridType = NUMBER;
    } else if (row.alias === EXIT_DATE_ALIAS) {
      gridType = GRID_DATE;
    }

    cells[key] = {
      key,
      columnLegend,
      tooltipMessages: row.tooltipMessages,
      alias: row.titleAlias || '',
      value: row.name,
      readOnly: true,
      className: rowLabelClassName,
      valueViewer: undefined,
      dataEditor: undefined,
      expr: undefined,
      style: undefined,
    };

    const valueCell: ICell = {
      value: row.value,
      tooltipMessages: [],
      key: valueKey,
      columnLegend: valueColumnLegend,
      className: contentCellClassName,
      alias: row.alias,
      readOnly: row.readOnly ?? row.alias === FIRM_TOTAL_EXIT_PROCEEDS_ALIAS,
      valueViewer: row.valueViewer,
      dataEditor: row.dataEditor,
      gridDateComponent: row.gridDateComponent,
      format: row.format,
      customFormat: row.customFormat,
      gridType: row.alias === FIRM_TOTAL_EXIT_PROCEEDS_ALIAS ? NUMBER : gridType,
      expr: row.alias === FIRM_TOTAL_EXIT_PROCEEDS_ALIAS ? row.expr : undefined,
      customKey: row.customKey,
      style: row.style,
    };
    cells[valueKey] = valueCell;

    const foreignValueCell: ICell = {
      value: foreignValue,
      // Override the row-level base formula for the converted firm total.
      expr: row.alias === FIRM_TOTAL_EXIT_PROCEEDS_ALIAS ? foreignValue : undefined,
      tooltipMessages: [],
      key: foreignValueKey,
      columnLegend: foreignValueColumnLegend,
      className: contentCellClassName,
      alias: `${row.alias}_foreign`,
      readOnly: true,
      format: row.format,
      customFormat: row.customFormat,
      gridType,
      style: row.style,
      valueViewer: foreignValueViewer,
    };

    switch (row.alias) {
      case EXIT_ENTERPRISE_VALUE_ALIAS:
        enterpriseValueCell = valueCell;
        foreignValueCell.alias = FUND_EXIT_ENTERPRISE_ALIAS;
        foreignValueCell.readOnly = row.foreignValue === null;
        break;
      case EXIT_EQUITY_ALIAS:
        equityValueCell = valueCell;
        foreignValueCell.alias = FUND_EXIT_EQUITY_ALIAS;
        foreignValueCell.readOnly = row.foreignValue === null;
        break;
      case PLUS_CASH_ALIAS:
        plusCashCell = valueCell;
        foreignValueCell.alias = FOREIGN_PLUS_CASH_ALIAS;
        foreignValueCell.readOnly = row.foreignValue === null;
        break;
      case LESS_DEBT_ALIAS:
        lessDebtCell = valueCell;
        foreignValueCell.alias = FOREIGN_LESS_DEBT_ALIAS;
        foreignValueCell.readOnly = row.foreignValue === null;
        break;
      case FOREIGN_EXCHANGE_RATE_BASE_ALIAS:
        foreignValueCell.alias = FOREIGN_EXCHANGE_RATE_TARGET_ALIAS;
        foreignValueCell.readOnly = row.readOnly ?? false;
        break;
      case CURRENCY_SELECTOR_ALIAS:
        foreignValueCell.valueViewer = WaterfallFXRowLabel;
        break;
      default:
        break;
    }

    cells[foreignValueKey] = foreignValueCell;
  });

  if (enterpriseValueCell?.key && equityValueCell?.key && plusCashCell?.key && lessDebtCell?.key) {
    if (enterpriseValueCell.value && !equityValueCell.value) {
      equityValueCell.expr = `=${getEquityValueFormula(enterpriseValueCell.key, plusCashCell.key, lessDebtCell.key)}`;
    }
    if (equityValueCell.value && !enterpriseValueCell.value) {
      enterpriseValueCell.expr = `=${getEnterpriseValueFormula(
        equityValueCell.key,
        plusCashCell.key,
        lessDebtCell.key
      )}`;
    }
  }

  return cells;
};

export default InputSheetParser;
